import { html, Page, View } from 'rune-ts';
import { BannerTypeS } from '../../../features/Banner/outbound/share';
import { EXCLUDE_MAKER_MODULE } from '../../../shared/constant';
import { footer_links } from '../../components/cells/Footer/constant';
import { LayoutView } from '../../components/cells/Layout/LayoutView';
import { QuickMenuData, QuickMenuListView } from '../../components/cells/QuickMenu/QuickMenu';
import homeClass from './Main.module.scss';
import { CastOnAir, CastOnAirProps } from './views/CastOnAir/CastOnAir';
import { LimitedEdition, LimitedEditionProps } from './views/LimitedEdition/LimitedEdition';
import { DomainBannerView } from '@services/MarppleShop/renderApp/pages/Main/views/DomainBanner/DomainBanner.ts';
import { BaseLayoutOptions } from '../../../features/Core/service';
import { ClientPopupProps, Popup } from '../../../shared/components/cells/Popup/Popup';
import {
  RecentlySoldProductSection,
  RecentlySoldProductSectionData,
} from './views/RecentlySoldProductSection/RecentlySoldProductSection';
import { htmlIf } from '../../../shared/util';
import { ProductCardViewData } from '../../../features/ProductList/type';
import { FloatingButton_recent_product } from '../../components/cells/FloatingBox/FloatingButtons/FloatingButton_recent_product';
import { FloatingBox } from '../../components/cells/FloatingBox/FloatingBox';
import { CreatorRankingList, CreatorRankingListProps } from './views/CreatorRankingList/CreatorRankingList';
import {
  BrandCommerceSectionData,
  BrandCommerceSectionView,
} from '@services/MarppleShop/renderApp/pages/Main/views/BrandCommerceSection/BrandCommerceSection.ts';
import { makeHomeScreenNavigate } from '@services/MarppleShop/shared/app/navigate.ts';
import { PopularTopNProductSectionView } from '@services/MarppleShop/renderApp/pages/Main/views/PopularTopNProductSection/PopularTopNProductSection.ts';
import { CateRootGnbMoView } from '@services/MarppleShop/renderApp/components/cells/CateRootGnbMo/CateRootGnbMoView.ts';
import { createGoodsUrlBuilder } from '@services/MarppleShop/features/ProductList/util';
import { FundingListResponse } from '@services/MarppleShop/features/Funding';
import { MainPageFundingSection } from '@services/MarppleShop/renderApp/pages/Main/views/FundingSection/MainPageFundingSection.ts';
import { ICategory } from '../../../../../shared/mps-shared/domain/category/types.ts';
import { GlobalTimerManager } from '@services/MarppleShop/shared/util/timer';
import { CurationListItem } from '@services/MarppleShop/features/Curation/type';
import { MainPageCurationListView } from '@services/MarppleShop/renderApp/pages/Curation/components/MainPageCurationListView.ts';

export interface HomePageData {
  banners: BannerTypeS.MainBannerData[];
  quick_menus: [QuickMenuData[], QuickMenuData[]]; // 두줄임을 표현
  limited_edition: LimitedEditionProps;
  realtime_best: {
    products: ProductCardViewData[];
  };
  curations: CurationListItem[];
  cast_on_air: CastOnAirProps;
  layout_options: BaseLayoutOptions;
  popup: ClientPopupProps;
  recently_sold: RecentlySoldProductSectionData;
  creator_ranking_list_props: CreatorRankingListProps;
  recent_product?: ProductCardViewData;
  brand_commerce: BrandCommerceSectionData;
  mps_category_infos: ICategory[];
  adult_check_needed: boolean;
  funding_lists: FundingListResponse;

  /**
   * 시간 동기화를 위해 서버에서 타임스템프를 내려줌
   */
  server_timestamp: number;

  readonly query: {
    cate_list_id?: number;
  };

  goods_cate_root_id: number;
}

class MainBodyView extends View<HomePageData> {
  protected override onRender(): void {
    super.onRender();

    const globalTimer = GlobalTimerManager.getInstance();
    globalTimer.start(1000);
  }

  protected override onUnmount(): void {
    super.onUnmount();

    const globalTimer = GlobalTimerManager.getInstance();
    globalTimer.stop();
  }

  protected override template() {
    const {
      banners,
      quick_menus,
      limited_edition,
      realtime_best,
      curations,
      cast_on_air,
      popup,
      recently_sold,
      creator_ranking_list_props,
      brand_commerce,
      funding_lists,
    } = this.data;
    const is_mobile = this.data.layout_options.is_mobile;

    return html`<div class="${homeClass.container} ${EXCLUDE_MAKER_MODULE}">
      ${is_mobile ? new CateRootGnbMoView({ cate_root_code: 'RECOMMEND' }) : ''}

      <!-- 배너 -->
      ${banners.length > 2
        ? html`<div class="${homeClass.domainBannerContainer}">
            ${new DomainBannerView({ banners, is_mobile })}
          </div>`
        : ''}
      <div data-is-main="true" class="${homeClass.homeContainer}">
        <div class="${homeClass.quick_menu_wrap}">
          ${new QuickMenuListView(quick_menus, {
            size: is_mobile ? 'small' : 'medium',
            use_scroll_snap: is_mobile,
          })}
        </div>

        <!-- 인기 랭킹 Top 100 -->
        ${realtime_best.products.length > 0
          ? new PopularTopNProductSectionView(
              {
                section_title: {
                  title: is_mobile ? ET('mps2::main::top_100::title_mo') : ET('mps2::main::top_100::title'),
                  sub_title: '',
                  see_more_link: createGoodsUrlBuilder().build(),
                },
                products: this.data.realtime_best.products,
              },
              { is_mobile },
            )
          : ''}

        <!-- 주목할 만한 펀딩 프로젝트 -->
        ${funding_lists.data.length > 0 && ET.lang === 'kr'
          ? new MainPageFundingSection({
              funding_lists,
              is_mobile,
              server_timestamp: this.data.server_timestamp,
              userId: this.data.layout_options.user?.id,
              adult_check_needed: this.data.adult_check_needed,
            })
          : ''}

        <!-- 방금, 막 판매된 실시간 판매 상품 -->
        ${htmlIf(
          new RecentlySoldProductSection(recently_sold, { is_mobile }),
          RecentlySoldProductSection.isVisible(recently_sold),
        )}

        <!-- 실시간 크리에이터 랭킹 -->
        ${creator_ranking_list_props.creator_rankings.length > 0
          ? new CreatorRankingList(creator_ranking_list_props)
          : ''}

        <!-- 마플샵 한정 상품 랭킹 -->
        ${limited_edition.products.length > 0
          ? new LimitedEdition(
              limited_edition,
              { is_mobile, is_see_more_link_visible: false },
              {
                getTitle: () => ET('mps2::main::limited_edition::title'),
                // 더보기 링크 삭제되었는데 혹시 몰라 우선 남겨둠
                getSeeMoreLink: () => {
                  return createGoodsUrlBuilder().build();
                },
                getNavigate: () => {
                  return makeHomeScreenNavigate();
                },
              },
            )
          : ''}

        <!-- 마플샵 큐레이션 -->
        ${curations.length > 0 ? new MainPageCurationListView({ curations, is_mobile }) : null}

        <!-- 크리에이터 x 브랜드 상품 -->
        ${BrandCommerceSectionView.isVisible(brand_commerce)
          ? new BrandCommerceSectionView(brand_commerce, {
              is_mobile,
            })
          : ''}

        <!-- Cast on Air -->
        ${cast_on_air.lives.length > 0 ? new CastOnAir(cast_on_air) : ''}
      </div>

      ${popup.popups.length > 0 ? new Popup(popup) : ''}
    </div>`;
  }
}

export class MainPage extends Page<HomePageData> {
  protected override template() {
    const { layout_options } = this.data;

    return html`
      <div>
        ${new LayoutView(
          {
            body: new MainBodyView(this.data),
            footer_data: { ...footer_links, lang: ET.lang, is_mobile: layout_options.is_mobile },
          },
          {
            require_top_arrow_button: true,
            full_width: true,
            has_bottom_tab_bar: true,
            selected_bottom_tab_idx: 0,
            floating_box: {
              recent_product: FloatingButton_recent_product.makeProps(this.data.recent_product),
              async renderHook(view: FloatingBox) {
                await FloatingBox.refreshRecentProductFloatingButton(view);
              },
            },
            ...layout_options,
          },
        )}
      </div>
    `;
  }
}
